import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { obtenerPermisosUsuario, actualizarPermisosUsuario } from '../../../../services/usuariosService';
import { showToast } from '../../../../utils/showToast';

const moduloOrder = ['General', 'Seguridad', 'Operativo', 'Academico', 'Financiero', 'RRHH'];

function ordenarModulos(modulos) {
  const ordenados = moduloOrder.filter(m => modulos.includes(m));
  return [...ordenados, ...modulos.filter(m => !ordenados.includes(m))];
}

function agrupar(permisos) {
  const map = {};
  for (const p of permisos) {
    const mod = p.modulo || 'General';
    const sub = p.submodulo || mod;
    if (!map[mod]) map[mod] = {};
    if (!map[mod][sub]) map[mod][sub] = [];
    map[mod][sub].push(p);
  }
  return map;
}

function filtrarGrupos(grouped, term) {
  if (!term.trim()) return grouped;
  const q = term.toLowerCase();
  const result = {};
  for (const [mod, subs] of Object.entries(grouped)) {
    for (const [sub, permisos] of Object.entries(subs)) {
      const matches = permisos.filter(p =>
        `${p.nombre} ${p.codigo} ${p.submodulo || ''}`.toLowerCase().includes(q) ||
        mod.toLowerCase().includes(q) || sub.toLowerCase().includes(q)
      );
      if (matches.length) {
        if (!result[mod]) result[mod] = {};
        result[mod][sub] = matches;
      }
    }
  }
  return result;
}

export default function UsuarioPermisosTab({ usuarioId }) {
  const [datos, setDatos]           = useState(null);
  const [cargando, setCargando]     = useState(false);
  const [guardando, setGuardando]   = useState(false);
  const [seleccion, setSeleccion]   = useState(new Set());
  const [busqueda, setBusqueda]     = useState('');
  const [moduloActivo, setModuloActivo] = useState('');

  const cargar = useCallback(async () => {
    if (!usuarioId) return;
    setCargando(true);
    try {
      const data = await obtenerPermisosUsuario(usuarioId);
      setDatos(data);
      setSeleccion(new Set(data.ids_individuales));
    } catch {
      showToast({ type: 'danger', message: 'Error al cargar los permisos del usuario' });
    } finally {
      setCargando(false);
    }
  }, [usuarioId]);

  useEffect(() => { cargar(); }, [cargar]);

  const idsRol = useMemo(() => new Set(datos?.ids_rol || []), [datos]);

  const grouped = useMemo(() => agrupar(datos?.todos || []), [datos]);

  const visible = useMemo(() => filtrarGrupos(grouped, busqueda), [grouped, busqueda]);

  const modulos = useMemo(() => ordenarModulos(Object.keys(visible)), [visible]);

  const modulosConConteo = useMemo(() =>
    modulos.map(mod => {
      const total = Object.values(visible[mod] || {})
        .reduce((acc, lista) => acc + lista.length, 0);
      return { mod, total };
    }), [modulos, visible]);

  useEffect(() => {
    if (!modulosConConteo.length) { setModuloActivo(''); return; }
    const nombres = modulosConConteo.map(m => m.mod);
    if (!moduloActivo || !nombres.includes(moduloActivo)) setModuloActivo(nombres[0]);
  }, [modulosConConteo, moduloActivo]);

  const togglePermiso = (id) => {
    if (idsRol.has(id)) return;
    setSeleccion(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleGuardar = async () => {
    setGuardando(true);
    try {
      await actualizarPermisosUsuario(usuarioId, [...seleccion]);
      showToast({ type: 'success', message: 'Permisos individuales actualizados' });
    } catch {
      showToast({ type: 'danger', message: 'Error al guardar los permisos' });
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) return (
    <div className="text-center py-4">
      <span className="spinner-border spinner-border-sm text-primary" />
    </div>
  );

  if (!datos) return null;

  const totalExtras = seleccion.size;
  const submodulos = visible[moduloActivo] || {};

  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3 gap-2 flex-wrap">
        <small className="text-muted">
          Permisos del rol en gris (no editables). Marca los extras para este usuario.
        </small>
        <button type="button" className="btn btn-primary btn-sm flex-shrink-0"
          onClick={handleGuardar} disabled={guardando}>
          {guardando
            ? <><span className="spinner-border spinner-border-sm me-1" />Guardando…</>
            : <><i className="bi bi-floppy me-1" />Guardar ({totalExtras} extras)</>}
        </button>
      </div>

      {/* Buscador */}
      <div className="mb-3">
        <input type="text" className="form-control form-control-sm"
          placeholder="Buscar permiso…"
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
        />
      </div>

      {/* Nav pills de módulos */}
      <div className="mb-3 overflow-auto">
        <div className="nav nav-pills gap-2 flex-nowrap">
          {modulosConConteo.map(({ mod, total }) => (
            <button key={mod} type="button"
              className={`btn btn-sm d-flex align-items-center gap-2 ${moduloActivo === mod ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setModuloActivo(mod)}>
              <span>{mod}</span>
              <span className="badge bg-light text-dark">{total}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Submodulos y permisos */}
      {!modulos.length ? (
        <p className="text-muted small text-center py-3">Sin resultados para "{busqueda}"</p>
      ) : (
        <div className="p-3 border rounded-3" style={{ maxHeight: 340, overflowY: 'auto' }}>
          {Object.entries(submodulos).map(([sub, permisos]) => (
            <div key={sub} className="mb-3">
              <div className="fw-semibold small mb-2 border-bottom pb-1">{sub}</div>
              <div className="row g-2">
                {permisos.map(p => {
                  const esDeRol      = idsRol.has(p.id);
                  const esIndividual = seleccion.has(p.id);
                  return (
                    <div key={p.id} className="col-md-4 col-lg-3">
                      <label className={`d-flex gap-2 align-items-start p-2 border rounded-3 w-100 ${esDeRol ? 'opacity-50' : ''}`}
                        style={{ cursor: esDeRol ? 'default' : 'pointer' }}>
                        <input type="checkbox" className="form-check-input mt-1 flex-shrink-0"
                          checked={esDeRol || esIndividual}
                          disabled={esDeRol}
                          onChange={() => togglePermiso(p.id)}
                        />
                        <div>
                          <div className={`small ${esIndividual && !esDeRol ? 'fw-semibold text-primary' : ''}`}>
                            {p.nombre}
                          </div>
                          {esIndividual && !esDeRol && (
                            <span className="badge bg-primary-subtle text-primary" style={{ fontSize: 9 }}>extra</span>
                          )}
                        </div>
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
